import { Form, getIn, useFormikContext } from 'formik';
import { FormikDebounce } from '../common/form/FormikDebounce';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '../common/form/Input';
import { FormContainer } from '../common/form/FormContainer';
import * as Yup from 'yup';
import { validations } from '../../utils/validations';
import { DatePicker } from '../common/form/DatePicker';
import { SubmitButton } from '../common/form/SubmitButton';
import { EmployeeAutocomplete } from '../common/form/EmployeeAutocomplete';
import { InvitationCreateForm } from '../../models/invitation';
import { InvitationAttachments } from './InvitationAttachments';

import { InvitationButtonsContainer } from './InvitationButtonsContainer';
import { useBusinessSettings } from '../../hooks/useBusinessSettings';
import { useAppSelector } from '../../store';
import { selectCompanyCustomization } from '../../store/entities/companyCustomization';
import { Box, Collapse, FormControlLabel, Switch } from '@mui/material';
import { QcsMinimalistToggle } from '../common/form/QcsMinimalistToggle';
import { QcsTimePicker } from '../common/form/QcsTimePicker';
import { ResponsiveRow } from '../common/form/ResponsiveRow';
import { ShowMoreToggle } from '../common/form/ShowMoreToggle';
import QcsAlert from '../common/basic/QcsAlert';

const IsVisitorToggle: FC = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<InvitationCreateForm>();
  const companyCustomization = useAppSelector(selectCompanyCustomization);

  const options = [
    { value: false, label: t('invitation.create.contractor') },
    { value: true, label: t('invitation.create.visitor') },
  ];

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
      <QcsMinimalistToggle
        options={options}
        value={values.isVisitor}
        onChange={(val) => {
          setFieldValue('isVisitor', val);
          setFieldValue('supplier', null);
          setFieldValue('responsiblePerson', null);
          setFieldValue(
            'primaryVisitor',
            val
              ? {
                  name: '',
                  lastName: '',
                  phone: '',
                  email: '',
                  language: companyCustomization?.defaultLanguage,
                }
              : null,
          );
          setFieldValue('addWorkerToInvitation', false);
          setFieldValue('visitorCompanyName', null);
          setFieldValue('additionalVisitors', []);
          setFieldValue('activities', []);
          setFieldValue('createHandoverProtocol', false);
          setFieldValue('entryRequestRequired', false);
        }}
      />
      {values.isVisitor && (
        <QcsAlert
          severity="info"
          sx={{ py: 0, minHeight: 0, alignItems: 'center', '& .MuiAlert-message': { py: '0.4rem' }, '& .MuiAlert-icon': { py: 0 } }}
        >
          {t('invitation.create.visitorWarning')}
        </QcsAlert>
      )}
    </Box>
  );
};

const SettingsToggles: FC<{ businessSettings: ReturnType<typeof useBusinessSettings> }> = ({ businessSettings }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<InvitationCreateForm>();

  const toggles = [
    { name: 'disableVisitationNotification', label: t('common.disableVisitationNotification'), show: true },
    {
      name: 'createHandoverProtocol',
      label: t('invitation.createHandoverProtocol'),
      show: !!businessSettings?.workplaceHandoverEnabled && !values.isVisitor,
    },
    { name: 'turnstilesActive', label: t('common.turnstilesActive'), show: businessSettings?.turnstilesEnabled },
    { name: 'entryRequestRequired', label: t('invitation.entryRequestRequired'), show: businessSettings?.allowEntryRequests && !values.isVisitor },
  ].filter((toggle) => toggle.show);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {toggles.map((toggle) => (
        <FormControlLabel
          key={toggle.name}
          control={<Switch checked={!!getIn(values, toggle.name)} onChange={(_, checked) => setFieldValue(toggle.name, checked)} />}
          label={toggle.label}
        />
      ))}
    </Box>
  );
};

interface Props {
  invitationData: InvitationCreateForm;
  handleSubmit: (data: InvitationCreateForm) => void;
}

export const InvitationCreateStep1: FC<Props> = ({ invitationData, handleSubmit }) => {
  const { t } = useTranslation();
  const businessSettings = useBusinessSettings();
  const [showMore, setShowMore] = useState(false);

  return (
    <FormikDebounce<InvitationCreateForm>
      initialValues={invitationData}
      validationSchema={Yup.object().shape({
        name: validations.stringRequired(t),
        employee: validations.objectRequired(t),
        validFrom: validations.stringDateNotPastRequired(t),
        validTo: Yup.string()
          .nullable()
          .when('validFrom', ([validFrom], schema) =>
            schema.test({
              test: (validTo: string | null | undefined) => {
                return !validTo || validFrom <= validTo;
              },
              message: t('errors.dateToBeforeFrom')!,
            }),
          ),
      })}
      onSubmit={handleSubmit}
    >
      <Form>
        <FormContainer>
          <IsVisitorToggle />
          <Input name="name" label={t('invitation.name')} required />
          <EmployeeAutocomplete name="employee" label={t('invitation.employee')} required filterOutNonHumanRoles={true} />
          <ResponsiveRow>
            <DatePicker name="validFrom" label={t('training.validFrom')} disablePast required />
            <DatePicker name="validTo" label={t('training.validTo')} disablePast />
            <QcsTimePicker label={t('invitation.earliestEntryTime')} name="earliestEntryTime" />
          </ResponsiveRow>
          <SettingsToggles businessSettings={businessSettings} />

          <ShowMoreToggle expanded={showMore} onToggle={() => setShowMore((prev) => !prev)} />
          <Collapse in={showMore}>
            <FormContainer>
              <Input name="note" label={t('invitation.note')} multiline={true} rows={4} maxLength={5000} />
              <Input name="noteInternal" label={t('invitation.noteInternal')} multiline={true} rows={4} maxLength={5000} />
              <InvitationAttachments titleVariant="h5" />
            </FormContainer>
          </Collapse>

          <InvitationButtonsContainer>
            <div />
            <div style={{ width: '100%' }} />
            <SubmitButton text={t('common.nextStep')} />
          </InvitationButtonsContainer>
        </FormContainer>
      </Form>
    </FormikDebounce>
  );
};
